(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const timing = { duration: 280, easing: 'cubic-bezier(.22,.61,.36,1)' };

  const initHeader = () => {
    const header = document.querySelector('.site-header');
    if (!header || header.dataset.motionReady) return;
    header.dataset.motionReady = 'true';
    let scheduled = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 12);
      scheduled = false;
    };
    window.addEventListener('scroll', () => {
      if (!scheduled) {
        scheduled = true;
        requestAnimationFrame(update);
      }
    }, { passive: true });
    update();
  };

  const initReveals = scope => {
    const selectors = [
      '.section-heading',
      '.category-card',
      '.seasonal-campaign__content',
      '.seasonal-campaign__products',
      '.product-card',
      '.trust-grid > div',
      '.faq__items',
      '.newsletter > .container'
    ];
    const elements = [...scope.querySelectorAll(selectors.join(','))]
      .filter(element => !element.dataset.motionReveal);
    if (!elements.length) return;

    elements.forEach(element => {
      element.dataset.motionReveal = 'true';
      element.classList.add('motion-reveal');
      const parent = element.parentElement;
      const siblings = parent ? [...parent.children].filter(child => selectors.some(selector => child.matches?.(selector))) : [];
      const index = siblings.indexOf(element);
      if (index > 0) element.style.setProperty('--motion-delay', `${Math.min(index, 3) * 55}ms`);
    });

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      elements.forEach(element => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -7% 0px', threshold: .08 });
    elements.forEach(element => observer.observe(element));
  };

  const initDetails = scope => {
    scope.querySelectorAll('.faq details, .product-accordions details').forEach(details => {
      if (details.dataset.motionReady) return;
      details.dataset.motionReady = 'true';
      const summary = details.querySelector('summary');
      const answer = summary?.nextElementSibling;
      if (!summary || !answer) return;

      summary.addEventListener('click', event => {
        if (reduceMotion.matches || typeof details.animate !== 'function') return;
        event.preventDefault();
        if (details.classList.contains('is-animating')) return;
        details.classList.add('is-animating');
        const startHeight = `${details.offsetHeight}px`;

        if (!details.open) {
          details.open = true;
          const endHeight = `${summary.offsetHeight + answer.offsetHeight}px`;
          details.animate({ height: [startHeight, endHeight] }, timing).finished.finally(() => {
            details.classList.remove('is-animating');
            details.style.height = '';
          });
          answer.animate({ opacity: [0, 1], transform: ['translateY(-5px)', 'translateY(0)'] }, timing);
        } else {
          const endHeight = `${summary.offsetHeight}px`;
          details.classList.add('is-closing');
          details.animate({ height: [startHeight, endHeight] }, timing).finished.finally(() => {
            details.open = false;
            details.classList.remove('is-animating');
            details.classList.remove('is-closing');
            details.style.height = '';
          });
          answer.animate({ opacity: [1, 0], transform: ['translateY(0)', 'translateY(-4px)'] }, { ...timing, duration: 190 });
        }
      });
    });
  };

  const initMobileMenu = scope => {
    scope.querySelectorAll('.mobile-nav').forEach(menu => {
      if (menu.dataset.motionReady) return;
      menu.dataset.motionReady = 'true';
      const summary = menu.querySelector('summary');
      if (!summary) return;
      summary.setAttribute('aria-expanded', String(menu.open));
      summary.addEventListener('click', event => {
        if (!menu.open) {
          document.body.classList.add('mobile-menu-open');
          return;
        }
        if (reduceMotion.matches) {
          document.body.classList.remove('mobile-menu-open');
          return;
        }
        event.preventDefault();
        document.body.classList.remove('mobile-menu-open');
        menu.classList.add('is-closing');
        summary.setAttribute('aria-expanded', 'false');
        window.setTimeout(() => {
          menu.open = false;
          menu.classList.remove('is-closing');
        }, 180);
      });
      menu.addEventListener('toggle', () => {
        if (!menu.classList.contains('is-closing')) summary.setAttribute('aria-expanded', String(menu.open));
        if (!menu.open) document.body.classList.remove('mobile-menu-open');
      });
    });
  };

  const initCartFeedback = () => {
    const drawerContent = document.querySelector('#cart-drawer-content');
    if (!drawerContent || drawerContent.dataset.motionReady) return;
    drawerContent.dataset.motionReady = 'true';
    const settle = () => {
      drawerContent.classList.remove('is-updating');
      drawerContent.removeAttribute('aria-busy');
    };
    const observer = new MutationObserver(settle);
    observer.observe(drawerContent, { childList: true, subtree: true });
    document.addEventListener('click', event => {
      if (event.target.closest('[data-cart-change]')) {
        drawerContent.classList.add('is-updating');
        drawerContent.setAttribute('aria-busy', 'true');
        window.setTimeout(settle, 2000);
      }
    });
  };

  const init = (scope = document) => {
    initHeader();
    initReveals(scope);
    initDetails(scope);
    initMobileMenu(scope);
    initCartFeedback();
  };

  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
